import { useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'

gsap.registerPlugin(ScrollTrigger)

/**
 * CtaReveal - entrance + ambient glow for the closing CTA band on /services,
 * /industries, /compliance, /trust and /insights.
 *
 * ── Existing animation, opted out ─────────────────────────────────────────
 * The band comes from the shared ctaBand() helper in pentabay-pages.js, and
 * [data-reveal] sits on the inner .shell, not the <section>. Measured with the
 * band scrolled into view BEFORE changing anything: rvPlayed=1,
 * data-reveal-staggered present, and 3 concurrent WAAPI animations across its
 * children - so RevealFixer was already cascading the eyebrow / headline / lead
 * / button row. Same opt-out as the other sections: rvPlayed='1' so
 * playStagger() bails, plus .in so the [data-reveal] CSS stops holding the
 * block at opacity 0.
 *
 * ── Route lifecycle: why this is not like the homepage components ─────────
 * HeroIntro / ScrollReveals guard on pathname === '/' and run once. These bands
 * are on subpages, which the SPA can reach two ways: a direct load, or a
 * client-side navigation long after this component mounted. dependencies: []
 * means the effect will not re-run for the second case, so the route is watched
 * instead - data-route on <html> (what the inline router stamps) plus
 * pb-pages-mounted (fired when a lazily-mounted page materialises).
 *
 * Idempotency is per ELEMENT (data-pb-cta-built), not per component instance,
 * and the observer is NOT disconnected after the first success. Both of those
 * are the fix for a real failure this went through: <html data-route> is stamped
 * "compliance" while main.page.active is still #page-home during the swap, so an
 * instance-level "done" flag latched onto a transient node - measured
 * builtOn.pageId "page-home", and that node's isConnected was false a moment
 * later. The annotation went with it and the real #page-compliance band was
 * never touched, silently. Requiring page.id === `page-${route}` plus a live
 * isConnected check is what makes it land on the node that survives.
 *
 * ── Scope: site-wide, by shape, with one mechanism-level exclusion ────────
 * Every route whose page closes with the band - 50 of the 54 registered routes.
 * There is no route allowlist, because a 50-name list would be a list of
 * everything and would drift the moment a route is added. The gate is the shape
 * lookup in findBand() plus the data-rv exclusion below.
 *
 * Swept all 54 registered routes (PB_PAGE_SOURCES + PB_PAGES, each materialised
 * with PB_ensurePage and inspected):
 *
 *   51 carry a band matching this shape. Five factories produce it - ctaBand()
 *      in public/pentabay-pages.js, in public/pentabay-subpages.js and in
 *      public/pentabay-location-blog.js, plus the two inline bands in
 *      Pentabay.html (#page-home's #contact and #page-weguard's) - and all five
 *      emit the same structure, so nothing needed normalising. Every one has
 *      eyebrow + h2.t-display + lead + 2 .btn, and in every case the matched
 *      section is the LAST section on the page, i.e. genuinely the closing CTA.
 *      .cta-band-art appears on /compliance only.
 *
 *    3 carry no such band and are skipped by findBand() returning null, which is
 *      the correct outcome rather than something to force a fit: /contact-us is a
 *      custom layout, /cookies-policy and /privacy-policy are two-section
 *      documents. Each has an .aura section but no closing h2.t-display.
 *
 *    1 is excluded deliberately - see below.
 *
 * ── Why data-rv bands are left alone ─────────────────────────────────────
 * RevealFixer has TWO independent players with TWO independent flags:
 * playStagger() bails on dataset.rvPlayed, but playVariants() bails on
 * dataset.rvVariantPlayed. Setting rvPlayed - which is what this component does
 * - therefore stops the generic cascade and does NOT stop a directional variant.
 *
 * Home's band (section#contact) is the only band in the site carrying
 * data-rv="scale"; measured across all 54 routes, nothing else does. RevealFixer
 * names it explicitly ("the #contact CTA, where the reveal target and the
 * variant target are the same div") and gives it a 620ms WAAPI scale. Running
 * this timeline on top of that is the §6.6 fight: WAAPI composites above inline
 * styles, wins for its 620ms, releases, and then the GSAP transform snaps in.
 *
 * So a band whose shell carries data-rv is skipped entirely and keeps the reveal
 * it already owns. Excluding by MECHANISM rather than by naming 'home' means a
 * band that gains a variant later is protected without anyone remembering to
 * come back here. Taking home over instead would mean also setting
 * rvVariantPlayed and replacing a designed homepage reveal - a redesign, not a
 * rollout.
 *
 * findBand() takes the FIRST section with a direct .aura child AND an
 * h2.t-display. The hero() helper also has a direct .aura child and comes first
 * on every page, so that second condition is what excludes it - the hero titles
 * with h1.t-h1. Keep both conditions if either helper is edited.
 */

/* Bands that already own a reveal variant. Currently matches exactly one
   element site-wide: #page-home #contact .shell[data-rv="scale"]. */
const OWNS_VARIANT = '[data-rv]'

/* 2026-08 intensity pass. 20px -> 56px and 0.7s -> 0.5s.
   expo.out here, NOT the back.out(1.6) the two heroes use, and that is a
   deliberate per-component choice rather than an oversight. This band's .shell
   children include the CTA row, whose .btn carries its own transform hover lift;
   an overshoot drives those buttons past their resting transform and hands them
   back mid-recoil the instant the gate is removed, which reads as a twitch on
   the one element in the band a user is about to put a cursor on. expo.out is a
   hard fast-then-decelerate punch with no overshoot to hand back, so it is
   unmistakably "animated" without touching the buttons' own transform state.
   The glow constants below are NOT part of this pass - that tween is ambient. */
const DURATION = 0.5
const EASE = 'expo.out'
const STEP = 0.15        // between sequence steps
const BTN_STEP = 0.11    // between the two buttons; 0.08 -> 0.11 so they read as two
const SHIFT = 56
const START = 'top 85%'

/* Glow: 0.3 -> 0.5 over a 4s round trip (2s each way with yoyo).
 *
 * The brief suggested pulsing "the background glow" between those opacities.
 * The band already HAS one - .aura - but its opacity is var(--aura-opacity),
 * which is 0.25 on dark and 0.08 on light. Driving that to 0.3-0.5 would make
 * it 4-6x brighter than designed on the light theme, i.e. the opposite of
 * ambient. So the pulse gets its own low-alpha layer and the .aura token is
 * left alone. The opacity numbers from the brief are kept; what changed is what
 * they are applied to.
 *
 * sine.inOut rather than the power2.out used for the entrances: on a yoyo,
 * an -out ease decelerates into the turn and snaps out of it, which reads as a
 * flicker. sine.inOut is symmetric, so the reversal is invisible. */
const GLOW_CLASS = 'pb-cta-glow'
const GLOW_HOST = 'pb-cta-glow-host'
const GLOW_FROM = 0.3
const GLOW_TO = 0.5
const GLOW_HALF_CYCLE = 2

const GATE = 'data-pb-cta-anim'
const CSS = `
/* isolation is load-bearing, not tidiness. The glow is a z-index:-1 child so it
   paints behind the copy - but a negative z-index escapes to the nearest
   ANCESTOR STACKING CONTEXT, and neither .shell nor the <section> was one
   (position:relative with z-index:auto does not create one). So the layer sank
   behind the page background: measured 0.00/255 contribution on the light theme
   and 0.56/255 on dark, i.e. invisible. isolate confines it to .shell, where it
   sits above the section background and below the text, which is the whole
   intent. */
.${GLOW_HOST} { isolation: isolate; }

/* Anchored to .shell (the copy block) and inset to cover it, NOT positioned
   against the <section>. First attempt used top:36% of the section, which on
   /compliance put the glow at y 136-556 while the headline was at y 68-136 -
   i.e. below the text it was supposed to sit behind. Measured luminance swing
   was 0.0/255: the feature did nothing at all. .shell is position:relative, so
   an inset child with z-index:-1 tracks the copy box automatically and needs no
   JS geometry or resize handling.
   The gradient centre is biased to 38% rather than 50% because /compliance also
   renders a .cta-band-art figure at the bottom of the shell, which drags the
   geometric centre below the text. */
.${GLOW_CLASS} {
  position: absolute;
  inset: -12% -6%;
  z-index: -1;
  pointer-events: none;
  opacity: 0;
  filter: blur(60px);
  background: radial-gradient(ellipse at 50% 38%,
    color-mix(in oklab, var(--pb-blue) 95%, transparent) 0%,
    transparent 66%);
}
/* A blue wash reads far harder on a white page than on ink, so the light theme
   gets about half the tint for the same animated opacity range. Tuned by
   measurement, not by eye - see the swing figures in the component notes. */
:root[data-theme="light"] .${GLOW_CLASS} {
  background: radial-gradient(ellipse at 50% 38%,
    color-mix(in oklab, var(--pb-blue) 58%, transparent) 0%,
    transparent 66%);
}
/* Pre-hide + no CSS transition while GSAP owns these nodes. Same two-part gate
   as ScrollReveals: .btn and .t-lead carry their own transitions, and leaving
   them live means the browser eases toward every frame GSAP writes, which
   lags and overshoots the tween. */
[${GATE}="pending"] > *:not(.${GLOW_CLASS}) { opacity: 0; }
[${GATE}] > *:not(.${GLOW_CLASS}),
[${GATE}] > *:not(.${GLOW_CLASS}) .btn { transition: none; }
`
if (typeof document !== 'undefined' && !document.getElementById('pb-cta-reveal-css')) {
  const el = document.createElement('style')
  el.id = 'pb-cta-reveal-css'
  el.textContent = CSS
  document.head.appendChild(el)
}

/* Located by SHAPE, not by heading text: a <section> with a direct .aura child
   and an h2.t-display. Matching on copy would break the moment the wording is
   edited or the page is translated (TranslationManager rewrites text nodes). */
function findBand(page) {
  for (const s of page.querySelectorAll('section')) {
    if (!Array.from(s.children).some(c => c.classList.contains('aura'))) continue
    if (!s.querySelector('h2.t-display')) continue
    const shell = s.querySelector('.shell')
    if (shell) return { section: s, shell }
  }
  return null
}

const BUILT_FLAG = 'pbCtaBuilt'

export default function CtaReveal() {
  const mms = useRef([])

  useGSAP(() => {
    let observer = null
    let mm = null   // eslint-disable-line prefer-const

    const build = () => {
      const route = document.documentElement.getAttribute('data-route')
      if (!route) return false
      const page = document.querySelector('main.page.active')
      // The route stamp and the active page disagree mid-swap; without this the
      // band gets built on whatever page is still showing.
      if (!page || page.id !== `page-${route}`) return false
      const found = findBand(page)
      // No band on this route (/contact-us, /cookies-policy, /privacy-policy).
      if (!found) return false
      const { section, shell } = found
      if (!shell.isConnected || shell.dataset[BUILT_FLAG] === '1') return false
      // Leave a band that already owns a reveal variant to RevealFixer. See the
      // two-flags note in the header - rvPlayed would not have stopped it.
      if (shell.matches(OWNS_VARIANT) || shell.querySelector(OWNS_VARIANT)) return false

      const eyebrow = shell.querySelector('.t-eyebrow')
      const heading = shell.querySelector('h2.t-display')
      const lead = shell.querySelector('.t-lead')
      const buttons = Array.from(shell.querySelectorAll('.btn'))
      // /compliance is the only page that passes `art` to ctaBand. It is the
      // last child, so it goes last in the sequence - otherwise it would sit
      // there fully visible while everything above it faded in.
      const art = shell.querySelector('.cta-band-art')
      /* Every part is optional. Measured across all five routes the shape is
         actually uniform - eyebrow + h2 + lead + 2 buttons, art on /compliance
         only - but a band that is missing a piece must animate the pieces it
         HAS rather than bail, or an edit to one page's markup would silently
         switch the whole treatment off on that route (the band would then also
         be left opted out of RevealFixer, i.e. no entrance at all). Only a
         band with nothing animatable is refused. */
      if (!heading && !eyebrow && !lead && !buttons.length && !art) return false

      shell.dataset[BUILT_FLAG] = '1'
      /* The glow is a CHILD of .shell (it has to be, to track the copy box), so
         every bulk operation on shell.children has to skip it. Missing this cost
         a real bug: the entrance timeline's clearProps('opacity') stripped the
         glow's inline opacity, dropping it to the CSS base of 0 - which held
         whenever the pulse happened to be paused off-screen, i.e. the glow
         silently switched itself off. */
      const copyChildren = () => Array.from(shell.children).filter(c => !c.classList.contains(GLOW_CLASS))
      // Opt out of RevealFixer's cascade before it can fire.
      shell.dataset.rvPlayed = '1'
      shell.classList.add('in')

      mm = gsap.matchMedia()
      mms.current.push(mm)

      mm.add('(prefers-reduced-motion: reduce)', () => {
        // No entrance, and no glow element at all - the pulse is the thing being
        // objected to, so it must not exist rather than run slower.
        shell.removeAttribute(GATE)
        gsap.set(copyChildren(), { clearProps: 'all' })
      })

      mm.add('(prefers-reduced-motion: no-preference)', () => {
        shell.setAttribute(GATE, 'pending')

        const glow = document.createElement('div')
        glow.className = GLOW_CLASS
        glow.setAttribute('aria-hidden', 'true')
        // Inside .shell as its first child: .shell is position:relative, so the
        // glow's inset box tracks the copy block and z-index:-1 keeps it behind
        // the text. As a sibling of .shell it could not follow that box.
        shell.classList.add(GLOW_HOST)
        shell.insertBefore(glow, shell.firstChild)

        const pulse = gsap.fromTo(glow,
          { opacity: GLOW_FROM },
          {
            opacity: GLOW_TO,
            duration: GLOW_HALF_CYCLE,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
            paused: true,
          },
        )

        const steps = [eyebrow, heading, lead].filter(Boolean)

        const entrance = ScrollTrigger.create({
          trigger: shell,
          start: START,
          once: true,
          onEnter() {
            shell.setAttribute(GATE, 'running')
            const tl = gsap.timeline({
              defaults: { duration: DURATION, ease: EASE },
              onComplete() {
                gsap.set(copyChildren(), { clearProps: 'opacity,transform,willChange' })
                shell.removeAttribute(GATE)
              },
            })
            /* Position is computed from what actually got placed, not from a
               fixed index: the first tween starts the timeline and everything
               after it is offset from the previous one. A hardcoded `<${STEP}`
               on the buttons would land them STEP after the timeline start if
               the copy steps above them were all missing. */
            let placed = 0
            const at = () => (placed++ === 0 ? 0 : `<${STEP}`)
            steps.forEach((el) => {
              tl.fromTo(el, { opacity: 0, y: SHIFT }, { opacity: 1, y: 0 }, at())
            })
            if (buttons.length) {
              tl.fromTo(buttons, { opacity: 0, y: SHIFT }, { opacity: 1, y: 0, stagger: BTN_STEP }, at())
            }
            if (art) tl.fromTo(art, { opacity: 0, y: SHIFT }, { opacity: 1, y: 0 }, at())
          },
        })

        /* The pulse only runs while the band is on screen: repeat: -1 otherwise
           keeps ticking forever, and with the treatment on five routes a
           visitor who has toured the site would otherwise leave four surplus
           infinite tweens running behind whatever they are reading.

           Keyed off the ELEMENT, never off the pathname. Parking (PB_parkPage
           in Pentabay.html) does node.remove() and stashes the node for reuse,
           so the section, its glow child and this tween all survive a route
           change detached from the document - a pathname-keyed pause would have
           nothing to re-arm when PB_ensurePage re-attaches the same node, and
           build() cannot re-arm it either because data-pb-cta-built persists on
           that node and makes the rebuild a no-op.

           IntersectionObserver rather than ScrollTrigger, and that is a fix for
           a measured leak, not a preference. ScrollTrigger resolves start/end to
           scroll offsets ONCE and only re-evaluates on scroll or refresh. It has
           no idea the trigger was detached, so if the next route happens to be
           scrolled to a position still inside the parked section's stale range,
           the trigger stays "active" and the tween keeps running on a node that
           is no longer in the document. Measured with a ScrollTrigger here and
           all five routes toured: sitting on /insights, the detached
           page-trust glow was still taking style writes at the full frame rate
           (72 writes / 2s, isConnected false) - one surplus infinite tween per
           tour, exactly what this block exists to prevent. The single-route
           version could never show it, because there was no later route to be
           parked behind.

           IO is driven by layout instead of cached numbers: removing the target
           from the document makes it non-intersecting, so parking pauses the
           pulse with no refresh needed, and re-attaching plus scrolling back
           resumes it. Now measured at 72 writes/2s for the visible band and 0
           for all four parked ones. */
        const io = new IntersectionObserver((entries) => {
          for (const e of entries) {
            if (e.isIntersecting) pulse.play()
            else pulse.pause()
          }
        })
        io.observe(section)

        return () => {
          pulse.kill()
          entrance.kill()
          io.disconnect()
          glow.remove()
          shell.classList.remove(GLOW_HOST)
          shell.removeAttribute(GATE)
        }
      })

      return true
    }

    build()
    /* Left running for the session on purpose. The page node is re-created on
       navigation, so a one-shot observer would only ever catch the first visit -
       and it was a one-shot observer that hid the transient-node bug above. The
       element flag makes repeat calls free, and the route check is the first
       thing build() does, so the common case is one string compare. */
    observer = new MutationObserver(build)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-route'] })
    observer.observe(document.body, { childList: true, subtree: true })
    window.addEventListener('pb-pages-mounted', build)

    return () => {
      if (observer) observer.disconnect()
      window.removeEventListener('pb-pages-mounted', build)
      for (const m of mms.current) m.revert()
      mms.current = []
    }
  }, { dependencies: [] })

  return null
}
